import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchInterviewRequest,
  selectSessionData,
  selectLoading,
  selectError,
  selectSessionExpired,
} from "@store/slices/interviewSlice";

import Header from "@components/Layout/Header";
import IntroVideo from "@/components/Interview/IntroVideo";
import ErrorAlert from "@components/UI/ErrorAlert";

/**
 * InterviewIntro Page
 * Displays intro video/text from employer
 * Candidate must watch before proceeding to setup
 */
const InterviewIntro = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sessionData = useSelector(selectSessionData);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const sessionExpired = useSelector(selectSessionExpired);

  // Redirect to dedicated page if session has expired (410)
  useEffect(() => {
    if (sessionExpired) {
      navigate('/session-expired', { replace: true });
    }
  }, [sessionExpired, navigate]);

  // Fetch interview data on mount
  useEffect(() => {
    if (sessionToken) {
      dispatch(fetchInterviewRequest(sessionToken));
    }
  }, [dispatch, sessionToken]);

  // If no intro, skip directly to setup
  useEffect(() => {
    if (sessionData) {
      // Skip if:
      // - No intro_type OR
      // - intro_type is video but no video URL OR
      // - intro_type is text but no text
      const hasIntroContent =
        sessionData.intro_type &&
        ((sessionData.intro_type === 'video' && sessionData.intro_video_url) ||
         (sessionData.intro_type === 'text' && sessionData.intro_text));

      if (!hasIntroContent) {
        navigate(`/interview/${sessionToken}/setup`, { replace: true });
      }
    }
  }, [sessionData, sessionToken, navigate]);

  const handleIntroComplete = () => {
    navigate(`/interview/${sessionToken}/setup`);
  };

  // Loading state — show spinner whenever data is not yet available
  // (guards both the initial render before useEffect fires AND while fetching)
  if (!sessionData && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading interview...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="mt-8">
            <ErrorAlert 
              message={error} 
              actions={[
                {
                  label: 'Back to Home',
                  variant: 'primary',
                  onClick: () => navigate('/'),
                }
              ]}
            />
          </div>
        </main>
      </div>
    );
  }

  // Normal render with session data
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-sky-50">
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Page Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            Welcome to Your Interview
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to showcase your skills for{' '}
            <span className="font-semibold text-gray-900">
              {sessionData.job_title || 'this position'}
            </span>
          </p>
        </div>

        {/* Intro Video Component */}
        {sessionData.intro_type && (sessionData.intro_video_url || sessionData.intro_text) && (
          <IntroVideo
            introType={sessionData.intro_type}
            introVideoUrl={sessionData.intro_video_url}
            introText={sessionData.intro_text}
            onIntroComplete={handleIntroComplete}
            isLoading={loading}
            sessionToken={sessionToken}
          />
        )}
      </main>
    </div>
  );
};

export default InterviewIntro;
